import * as Dialog from "@radix-ui/react-dialog";
import { Cookie, Settings2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  OPEN_COOKIE_PREFERENCES_EVENT,
  readCookieConsent,
  saveCookieConsent,
} from "@/lib/cookie-consent";
import { updateFirebaseAnalyticsConsent } from "@/lib/firebase";

export function CookiePreferences() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const consent = readCookieConsent();
    setAnalytics(consent?.analytics ?? false);
    setShowBanner(!consent);

    const openSettings = () => {
      setAnalytics(readCookieConsent()?.analytics ?? false);
      setShowSettings(true);
    };

    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openSettings);
  }, []);

  async function choose(nextAnalytics: boolean) {
    setAnalytics(nextAnalytics);
    saveCookieConsent(nextAnalytics);
    await updateFirebaseAnalyticsConsent(nextAnalytics);
    setShowBanner(false);
    setShowSettings(false);
  }

  return (
    <>
      {showBanner && (
        <section
          className="fixed inset-x-2 bottom-2 z-[70] mx-auto min-w-0 max-w-4xl overflow-hidden rounded-xl border border-white/20 bg-black p-4 text-white shadow-[0_18px_70px_rgb(0,0,0,0.35)] sm:inset-x-5 sm:bottom-5 sm:p-6"
          aria-label="Preferencias de cookies"
        >
          <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 sm:h-9 sm:w-9">
                  <Cookie className="h-4 w-4" />
                </span>
                <h2 className="text-base font-semibold tracking-[-0.03em] sm:text-lg">
                  Tú eliges la analítica.
                </h2>
              </div>
              <p className="mt-2 text-sm leading-5 text-white/70 sm:hidden">
                Lo necesario funciona siempre. La analítica es opcional y no usamos publicidad.
              </p>
              <p className="mt-3 hidden max-w-2xl text-xs leading-5 text-white/65 sm:block">
                Usamos almacenamiento necesario para la cuenta, el carrito y tus preferencias.
                Firebase Analytics solo se activa si lo autorizas. No usamos cookies publicitarias.
                Consulta la{" "}
                <a href="/legal/cookies" className="font-semibold text-white underline">
                  política de cookies
                </a>{" "}
                y la{" "}
                <a href="/legal/privacidad" className="font-semibold text-white underline">
                  política de privacidad
                </a>
                .
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap md:justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-white/30 bg-black text-white hover:bg-white hover:text-black"
                onClick={() => setShowSettings(true)}
              >
                <Settings2 className="h-4 w-4" />
                Configurar
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-white/30 bg-black text-white hover:bg-white hover:text-black"
                onClick={() => void choose(false)}
              >
                Solo necesarias
              </Button>
              <Button
                type="button"
                size="sm"
                className="col-span-2 bg-white text-black hover:bg-white/85 sm:col-span-1"
                onClick={() => void choose(true)}
              >
                Aceptar analítica
              </Button>
            </div>
          </div>
        </section>
      )}

      <Dialog.Root open={showSettings} onOpenChange={setShowSettings}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-[81] max-h-[calc(100dvh_-_2rem)] w-[calc(100vw_-_2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-black bg-white p-6 text-black shadow-[0_24px_80px_rgb(0,0,0,0.3)] outline-none sm:p-8">
            <Dialog.Title className="text-2xl font-semibold tracking-[-0.04em]">
              Preferencias de cookies
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm leading-6 text-black/55">
              Puedes cambiar esta decisión cuando quieras desde el pie del sitio.
            </Dialog.Description>
            <Dialog.Close asChild>
              <button
                type="button"
                className="absolute right-4 top-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-black/15 transition hover:bg-black hover:text-white"
                aria-label="Cerrar preferencias"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>

            <div className="mt-7 space-y-3">
              <div className="rounded-xl border border-black/15 bg-[#f7f6f1] p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold">Necesarias</p>
                    <p className="mt-1 text-xs leading-5 text-black/55">
                      Cuenta, seguridad, carrito, moneda, catálogo y recuerdo de tus decisiones.
                    </p>
                  </div>
                  <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                    Siempre
                  </span>
                </div>
              </div>

              <label className="flex cursor-pointer items-start justify-between gap-5 rounded-xl border border-black/15 p-5">
                <span>
                  <span className="block text-sm font-semibold">Analítica</span>
                  <span className="mt-1 block text-xs leading-5 text-black/55">
                    Firebase Analytics nos ayuda a entender páginas, búsquedas, carrito y descargas
                    de forma agregada. No enviamos contraseñas ni datos de tarjeta.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(event) => setAnalytics(event.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-black"
                  aria-label="Permitir Firebase Analytics"
                />
              </label>
            </div>

            <div className="mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => void choose(false)}>
                Rechazar analítica
              </Button>
              <Button type="button" onClick={() => void choose(analytics)}>
                Guardar preferencias
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
